"use client"
import { type FragmentOf, readFragment } from "@opensea/graphql"
import {
  BottomSheetBody,
  BottomSheetFooter,
  BottomSheetHeader,
  BottomSheetHeaderTitle,
  Flex,
  Form,
  Media,
  Separator,
  SkeletonBlock,
  tv,
} from "@opensea/ui-kit"
import { useLocalStorage } from "@opensea/ui-kit/hooks"
import { useMemo } from "react"
import {
  Controller,
  useFormContext,
  useFormState,
  useWatch,
} from "react-hook-form"
import { useTranslations } from "use-intl"
import { Button } from "../../../../../components/Button"
import {
  CreateOrdersSummary,
  CreateOrdersSummarySkeleton,
} from "../../../../../components/CreateOrdersSummary"
import { ExpirationSelect } from "../../../../../components/ExpirationSelect"
import { useIsFormSubmitDisabled } from "../../../../../lib/form/valid"
import {
  DEFAULT_EXPIRATION,
  OFFER_EXPIRATION_CUSTOM_EXPIRATION_LOCAL_STORAGE,
  OFFER_EXPIRATION_LOCAL_STORAGE,
} from "../../../constants"
import type { CreateOfferFlowFormValues } from "../../types"
import {
  CreateOfferFlowHeaderActions,
  CreateOfferFlowHeaderActionsSkeleton,
} from "../CreateOfferFlowHeaderActions"
import {
  CreateOfferFlowTable,
  CreateOfferFlowTableSkeleton,
} from "../CreateOfferFlowTable/CreateOfferFlowTable"
import { CreateOfferForm_itemFragment } from "./CreateOfferForm.fragment"

type Item = FragmentOf<typeof CreateOfferForm_itemFragment>

type Props = {
  items: Item[]
  onSubmit: (values: CreateOfferFlowFormValues) => void
}

const footerVariants = tv({
  slots: {
    buttonContainer:
      "grid grid-cols-2 gap-3 pb-4 md:flex md:justify-end md:pb-0",
  },
})

export const CreateOfferForm = ({ items: itemKeys, onSubmit }: Props) => {
  const t = useTranslations("wallet.CreateOfferForm")
  const items = itemKeys.map(itemKey =>
    readFragment(CreateOfferForm_itemFragment, itemKey),
  )
  const { control, handleSubmit } = useFormContext<CreateOfferFlowFormValues>()
  const { errors } = useFormState({ control })
  const offers = useWatch({ control, name: "offers" })
  const firstOffer = offers[0]

  const tableItems = offers.flatMap(offer => {
    const matchedItem = items.find(item => item.id === offer.id)
    return matchedItem ? [matchedItem] : []
  })
  const [_, setOfferExpiration] = useLocalStorage(
    OFFER_EXPIRATION_LOCAL_STORAGE,
    DEFAULT_EXPIRATION,
  )
  const [__, setOfferExpirationCustom] = useLocalStorage<string | undefined>(
    OFFER_EXPIRATION_CUSTOM_EXPIRATION_LOCAL_STORAGE,
    undefined,
  )

  const isFormSubmitDisabled = useIsFormSubmitDisabled(control)

  const footer = useMemo(() => {
    return (
      <Flex className={footerVariants().buttonContainer()}>
        <Controller
          control={control}
          name="expiration"
          render={({ field }) => (
            <ExpirationSelect
              className="w-full"
              customDate={field.value.customExpiration}
              error={errors.expiration?.customExpiration?.message}
              onChange={value => {
                field.onChange({
                  ...field.value,
                  expirationType: value,
                })
                setOfferExpiration(value)
              }}
              onCustomDateChange={date => {
                field.onChange({
                  ...field.value,
                  customExpiration: date,
                })
                setOfferExpirationCustom(date.toISOString())
              }}
              value={field.value.expirationType}
            />
          )}
        />

        <Button disabled={isFormSubmitDisabled} type="submit">
          {t("reviewItemOffer", { count: items.length })}
        </Button>
      </Flex>
    )
  }, [
    control,
    errors,
    setOfferExpiration,
    setOfferExpirationCustom,
    t,
    isFormSubmitDisabled,
  ])

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <BottomSheetHeader className="justify-between">
        <BottomSheetHeaderTitle className="whitespace-nowrap">
          {t("createItemOffer", { count: items.length })}
        </BottomSheetHeaderTitle>
        <Media greaterThanOrEqual="md">
          <CreateOfferFlowHeaderActions
            offerSymbol={firstOffer?.offerSymbol || ""}
          />
        </Media>
      </BottomSheetHeader>

      <BottomSheetBody className="mt-2 pb-0">
        <Media lessThan="md">
          <CreateOfferFlowHeaderActions
            className="pb-4"
            offerSymbol={firstOffer?.offerSymbol || ""}
          />
        </Media>

        <CreateOfferFlowTable isReview={false} items={tableItems} />

        <Separator className="mt-1" />

        {items[0]?.collection ? (
          <CreateOrdersSummary
            adjustForFees={false}
            className="md:w-fit"
            collection={items[0].collection}
            creatorFeeEnforced={firstOffer?.isCreatorFeeRequired}
            currencyIdentifier={{
              contractAddress: items[0].collection.chain.offerCurrency.address,
              chainIdentifier: items[0].collection.chain.identifier,
            }}
            orderSide="offer"
            orders={offers}
            symbol={items[0].collection.chain.offerCurrency.symbol}
          />
        ) : null}

        <Media lessThan="md">{footer}</Media>
      </BottomSheetBody>

      <Media greaterThanOrEqual="md">
        <BottomSheetFooter>{footer}</BottomSheetFooter>
      </Media>
    </Form>
  )
}

type CreateOfferFormSkeletonProps = {
  count: number
}

export const CreateOfferFormSkeleton = ({
  count,
}: CreateOfferFormSkeletonProps) => {
  const t = useTranslations("wallet.CreateOfferForm")
  return (
    <>
      <BottomSheetHeader className="justify-between">
        <BottomSheetHeaderTitle>
          {t("createItemOffer", { count })}
        </BottomSheetHeaderTitle>
        <Media greaterThanOrEqual="md">
          <CreateOfferFlowHeaderActionsSkeleton count={count} />
        </Media>
      </BottomSheetHeader>
      <BottomSheetBody>
        <Media lessThan="md">
          <CreateOfferFlowHeaderActionsSkeleton
            className="pb-4"
            count={count}
          />
        </Media>
        <CreateOfferFlowTableSkeleton count={count} />
        <Separator className="mt-1" />
        <CreateOrdersSummarySkeleton orderSide="offer" />
      </BottomSheetBody>
      <BottomSheetFooter>
        <Flex className={footerVariants().buttonContainer()}>
          <SkeletonBlock className="h-8 w-full rounded md:w-44" />
          <SkeletonBlock className="h-8 w-full rounded md:w-24" />
        </Flex>
      </BottomSheetFooter>
    </>
  )
}
